// Bridges the /propiedades AdvancedFilters into /intercambio/propiedades
// ExchangeFilters via URL query params. Lets the user keep their search
// criteria when jumping from "what do I have?" to "what's in the network?".
//
// Two filter shapes don't fully overlap (Properties uses publish/share status,
// agent_ids, min-thresholds for baths/parking; Exchange has none of these).
// We silently drop the non-transferables — see the count helper below for
// what actually transfers.

import type { AdvancedFilters } from '../components/ui/PropertyFiltersModal';
import type { ExchangeFilters } from '../components/ui/ExchangeFiltersSlideOver';
import { defaultExchangeFilters } from '../components/ui/ExchangeFiltersSlideOver';

// Number of filters in `f` that have a clean target on the Exchange side.
// Used to drive the "Buscar en Intercambio" button visibility — if the user
// only set agent_ids or publishStatus, the button is hidden because clicking
// it would carry nothing.
export function countTransferableFilters(f: AdvancedFilters): number {
  let n = 0;
  if (f.operation && f.operation !== 'All') n++;
  if (f.propertyType && f.propertyType.length > 0) n++;
  if (f.minPrice || f.maxPrice) n++;
  if (f.bedrooms && f.bedrooms.length > 0) n++;
  if (f.buildUpSizeMin || f.buildUpSizeMax) n++;
  if (f.landSizeMin || f.landSizeMax) n++;
  if (f.amenities && f.amenities.length > 0) n++;
  if (f.department_id) n++;
  if (f.city_ids && f.city_ids.length > 0) n++;
  if (f.sector_ids && f.sector_ids.length > 0) n++;
  return n;
}

// Serialize transferable AdvancedFilters into a URL query string. Property
// type and amenity name lookups are passed in because Properties stores them
// as names while Exchange stores IDs — caller fetches once and reuses the
// maps for repeat clicks.
export function buildExchangeUrl(
  filters: AdvancedFilters,
  propertyTypeNameToId: Map<string, number>,
  amenityNameToId: Map<string, number>,
): string {
  const params = new URLSearchParams();

  if (filters.operation && filters.operation !== 'All') params.set('op', filters.operation);

  if (filters.propertyType && filters.propertyType.length > 0) {
    const ids = filters.propertyType
      .map((name) => propertyTypeNameToId.get(name))
      .filter((id): id is number => id != null);
    if (ids.length > 0) params.set('types', ids.join(','));
  }

  if (filters.minPrice) params.set('minPrice', filters.minPrice);
  if (filters.maxPrice) params.set('maxPrice', filters.maxPrice);
  if (filters.bedrooms && filters.bedrooms.length > 0) params.set('beds', filters.bedrooms.join(','));
  if (filters.buildUpSizeMin) params.set('buildMin', filters.buildUpSizeMin);
  if (filters.buildUpSizeMax) params.set('buildMax', filters.buildUpSizeMax);
  if (filters.landSizeMin) params.set('landMin', filters.landSizeMin);
  if (filters.landSizeMax) params.set('landMax', filters.landSizeMax);

  if (filters.amenities && filters.amenities.length > 0) {
    const ids = filters.amenities
      .map((name) => amenityNameToId.get(name))
      .filter((id): id is number => id != null);
    if (ids.length > 0) params.set('amenities', ids.join(','));
  }

  if (filters.department_id) params.set('dept', String(filters.department_id));
  if (filters.city_ids && filters.city_ids.length > 0) params.set('cities', filters.city_ids.join(','));
  if (filters.sector_ids && filters.sector_ids.length > 0) params.set('sectors', filters.sector_ids.join(','));

  // Signal to the receiving page to auto-open the filter panel so the user
  // can review what was carried over.
  params.set('openFilters', '1');

  return `/intercambio/propiedades?${params.toString()}`;
}

// Parse query string back into ExchangeFilters. Returns the parsed filters +
// whether the URL signaled a carry-over (any known filter param present, or
// openFilters=1). Used by ExchangePropertiesPage on mount.
export function parseExchangeFiltersFromQuery(search: string): {
  filters: ExchangeFilters;
  hasCarryOver: boolean;
} {
  const params = new URLSearchParams(search);
  const out: ExchangeFilters = { ...defaultExchangeFilters };
  let hit = false;

  const op = params.get('op');
  if (op === 'sale' || op === 'rent') {
    out.operation = op;
    hit = true;
  }

  const types = params.get('types');
  if (types) {
    out.propertyTypeIds = types.split(',').map(Number).filter((n) => !Number.isNaN(n));
    if (out.propertyTypeIds.length > 0) hit = true;
  }

  const minPrice = params.get('minPrice');
  const maxPrice = params.get('maxPrice');
  if (minPrice) { out.minPrice = minPrice; hit = true; }
  if (maxPrice) { out.maxPrice = maxPrice; hit = true; }

  const beds = params.get('beds');
  if (beds) {
    out.bedrooms = beds.split(',').map(Number).filter((n) => !Number.isNaN(n));
    if (out.bedrooms.length > 0) hit = true;
  }

  const buildMin = params.get('buildMin');
  const buildMax = params.get('buildMax');
  if (buildMin) { out.buildMinM2 = buildMin; hit = true; }
  if (buildMax) { out.buildMaxM2 = buildMax; hit = true; }

  const landMin = params.get('landMin');
  const landMax = params.get('landMax');
  if (landMin) { out.landMinM2 = landMin; hit = true; }
  if (landMax) { out.landMaxM2 = landMax; hit = true; }

  const amenities = params.get('amenities');
  if (amenities) {
    out.amenityIds = amenities.split(',').map(Number).filter((n) => !Number.isNaN(n));
    if (out.amenityIds.length > 0) hit = true;
  }

  const dept = params.get('dept');
  if (dept) {
    const n = Number(dept);
    if (!Number.isNaN(n)) { out.departmentId = n; hit = true; }
  }

  const cities = params.get('cities');
  if (cities) {
    out.cityIds = cities.split(',').map(Number).filter((n) => !Number.isNaN(n));
    if (out.cityIds.length > 0) hit = true;
  }

  const sectors = params.get('sectors');
  if (sectors) {
    out.sectorIds = sectors.split(',').map(Number).filter((n) => !Number.isNaN(n));
    if (out.sectorIds.length > 0) hit = true;
  }

  if (params.get('openFilters') === '1') hit = true;

  return { filters: out, hasCarryOver: hit };
}
